<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useTicketsStore } from 'dashboard/stores/tickets';
import { useTicketColumnsStore } from 'dashboard/stores/ticketColumns';
import TicketDialog from './TicketDialog.vue';
import TicketDetailDialog from './TicketDetailDialog.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const PRIORITY_ICON = {
  urgent: 'i-woot-priority-urgent',
  high: 'i-woot-priority-high',
  medium: 'i-woot-priority-medium',
  low: 'i-woot-priority-low',
};

const { t } = useI18n();
const store = useStore();
const ticketsStore = useTicketsStore();
const columnsStore = useTicketColumnsStore();

const agents = useMapGetter('agents/getAgents');
const accountLabels = useMapGetter('labels/getLabels');

const ticketDialogRef = ref(null);
const ticketDetailRef = ref(null);

const columns = computed(() => columnsStore.getColumns);

const conversationTickets = computed(() =>
  ticketsStore.getTicketsList
    .filter(ticket => ticket.conversation_id === Number(props.conversationId))
    .sort((a, b) => b.id - a.id)
);

const columnFor = ticket =>
  columns.value.find(c => c.id === ticket.ticket_column_id);

const openCreate = () => {
  ticketDialogRef.value?.open({ conversation: Number(props.conversationId) });
};
const openEdit = ticket => {
  ticketDetailRef.value?.open(ticket);
};

const onSave = async ({ id, ticket }) => {
  try {
    if (id) {
      await ticketsStore.update({ id, ticket });
      useAlert(t('TICKETS.UPDATE.SUCCESS'));
    } else {
      await ticketsStore.create({ ticket });
      useAlert(t('TICKETS.CREATE.SUCCESS'));
    }
  } catch {
    useAlert(id ? t('TICKETS.UPDATE.ERROR') : t('TICKETS.CREATE.ERROR'));
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  columnsStore.get();
  ticketsStore.get();
});
</script>

<template>
  <div class="flex flex-col gap-2 px-1 py-1">
    <ul v-if="conversationTickets.length" class="flex flex-col gap-1.5">
      <li
        v-for="ticket in conversationTickets"
        :key="ticket.id"
        class="flex flex-col gap-1 p-2 rounded-lg cursor-pointer bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6"
        @click="openEdit(ticket)"
      >
        <div class="flex items-center gap-1.5">
          <span class="text-xs shrink-0 text-n-slate-10 tabular-nums">
            #{{ ticket.display_id }}
          </span>
          <span class="flex-1 text-sm truncate text-n-slate-12">
            {{ ticket.title }}
          </span>
          <Icon
            v-if="ticket.priority"
            :icon="PRIORITY_ICON[ticket.priority]"
            class="size-4 shrink-0"
          />
        </div>
        <div class="flex items-center justify-between gap-2">
          <span
            class="inline-flex items-center gap-1 text-xs text-n-slate-11"
          >
            <span
              class="w-2 h-2 rounded-full shrink-0"
              :style="{ backgroundColor: columnFor(ticket)?.color || '#94a3b8' }"
            />
            {{ columnFor(ticket)?.name }}
          </span>
          <Avatar
            v-if="ticket.assignee"
            :name="ticket.assignee.name"
            :src="ticket.assignee.avatar_url"
            :size="18"
            rounded-full
          />
        </div>
      </li>
    </ul>
    <p v-else class="px-1 py-2 text-sm text-n-slate-11">
      {{ t('TICKETS.PANEL.EMPTY') }}
    </p>

    <Button
      :label="t('TICKETS.PANEL.CREATE')"
      icon="i-lucide-plus"
      variant="faded"
      color="slate"
      size="sm"
      class="w-full"
      @click="openCreate"
    />

    <TicketDialog
      ref="ticketDialogRef"
      :columns="columns"
      :agents="agents"
      @save="onSave"
    />
    <TicketDetailDialog ref="ticketDetailRef" />
  </div>
</template>
